import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, deleteDoc, doc, getDoc, setDoc, query, where } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';

const JoinRequests = () => {
  const [joinRequests, setJoinRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [agreeLoading, setAgreeLoading] = useState(false);
  const [disagreeLoading, setDisagreeLoading] = useState(false);
  const [user, userLoading] = useAuthState(auth);
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleLoading, setRoleLoading] = useState(true);

  // Check if the user is an admin
  useEffect(() => {
    const checkAdminRole = async () => {
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data().role === 'admin') {
          setIsAdmin(true);
        }
      }
      setRoleLoading(false);
    };
    if (!userLoading) {
      checkAdminRole();
    }
  }, [user, userLoading]);

  // Fetch join requests
  const fetchJoinRequests = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'joinRequests'));
      const requestsData = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        };
      });
      setJoinRequests(requestsData);
    } catch (err) {
      console.error('Error fetching join requests:', err);
      setError('Failed to load join requests: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && !roleLoading) {
      fetchJoinRequests();
    }
  }, [isAdmin, roleLoading]);

  const handleRowClick = (request) => {
    setSelectedRequest(request);
  };

  const handleClosePopup = () => {
    setSelectedRequest(null);
  };

  const sendEmail = async (to, subject, body) => {
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/send-contact-email`;
      console.log('Sending email to:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to,
          subject,
          message: body,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to send email: ${errorText} (Status: ${response.status})`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  };

  const handleDisagree = async (request) => {
    const confirmReject = window.confirm(`Are you sure you want to reject ${request.fullName}'s join request? This action cannot be undone.`);
    if (!confirmReject) return;

    try {
      setDisagreeLoading(true);

      const emailBody = `Dear ${request.fullName},\n\nWe regret to inform you that your join request to Medpredix has been rejected. If you have any questions or need further assistance, please contact our team at support@mediscanai.com.\n\nBest regards,\nMedpredix Team`;

      await sendEmail(request.email, 'Medpredix Join Request Rejected', emailBody);
      await deleteDoc(doc(db, 'joinRequests', request.id));
      setJoinRequests(joinRequests.filter((req) => req.id !== request.id));
      setSelectedRequest(null);

      toast.success('Join request rejected successfully.');
    } catch (err) {
      console.error('Error rejecting join request:', err);
      toast.error(err.message || 'Failed to reject join request.');
    } finally {
      setDisagreeLoading(false);
    }
  };

  const handleAgree = async (request) => {
    try {
      setAgreeLoading(true);
  
      const signInMethods = await fetchSignInMethodsForEmail(auth, request.email);
      console.log('Sign-in methods for', request.email, ':', signInMethods); // Debug log
      let userUid;
  
      if (signInMethods.length > 0) {
        console.warn('Email already in use. Fetching existing user.');
        const q = query(collection(db, 'users'), where('email', '==', request.email));
        const existingUser = await getDocs(q);
        if (!existingUser.empty) {
          userUid = existingUser.docs[0].id;
          toast.warn(`User with email ${request.email} already exists. Join request processed without creating a new account.`);
        } else {
          // Email exists in Authentication but not in Firestore; create a Firestore document
          console.warn('No Firestore user found for existing email. Creating new Firestore document.');
          const userCredential = auth.currentUser; // Note: This won't work here; we need the UID
          // Since we don't have the UID directly, we'll need to handle this differently
          // For now, assume we need to fetch UID via another method or skip creation
          toast.warn(`User ${request.email} exists in Authentication but not Firestore. Proceeding with new document creation.`);
  
          // We can't create a new Auth user here, so we need to link to the existing one
          // Ideally, we'd need the UID, but without sign-in, we'll create a placeholder UID
          userUid = request.email.replace(/[^a-zA-Z0-9]/g, ''); // Temporary UID based on email (not ideal)
          await setDoc(doc(db, 'users', userUid), {
            email: request.email,
            displayName: request.fullName,
            username: request.email.split('@')[0],
            role: 'user',
            status: 'active',
            plan: 'basic', 
            credits: 30, 
            lastCreditRenewal: new Date(), 
            predictionCounts: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
          }, { merge: true });
        }
      } else {
        const generateRandomPassword = () => {
          const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
          return Array.from(crypto.getRandomValues(new Uint32Array(12)))
            .map((value) => charset[value % charset.length])
            .join('');
        };
        const randomPassword = generateRandomPassword();
  
        try {
          const userCredential = await createUserWithEmailAndPassword(auth, request.email, randomPassword);
          userUid = userCredential.user.uid;
  
          const emailBody = `Dear ${request.fullName},\n\nWe are pleased to inform you that your join request to Medpredix has been approved! Your account has been created. Please log in using this email address and set your password to get started.\n\nYour temporary password is: ${randomPassword}\n\nBest regards,\nMedpredix Team`;
  
          await sendEmail(request.email, 'Medpredix Join Request Approved', emailBody);
        } catch (authError) {
          if (authError.code === 'auth/email-already-in-use') {
            const q = query(collection(db, 'users'), where('email', '==', request.email));
            const existingUser = await getDocs(q);
            if (!existingUser.empty) {
              userUid = existingUser.docs[0].id;
              toast.warn(`User with email ${request.email} already exists. Join request processed without creating a new account.`);
            } else {
              // Email exists in Auth but not Firestore; create a document
              console.warn('Creating Firestore document for existing Auth user.');
              userUid = request.email.replace(/[^a-zA-Z0-9]/g, ''); // Temporary UID (not ideal)
              await setDoc(doc(db, 'users', userUid), {
                email: request.email,
                displayName: request.fullName,
                username: request.email.split('@')[0],
                role: 'user',
                status: 'active',
                plan: 'basic', // Default to Basic plan
                credits: 30, // Initial 30 credits
                lastCreditRenewal: new Date(), 
                predictionCounts: 0,
                createdAt: new Date(),
                updatedAt: new Date(),
              }, { merge: true });
              toast.warn(`User ${request.email} synced with Firestore. Join request processed.`);
            }
          } else {
            throw authError;
          }
        }
      }
  
      // Add or update user document in Firestore (moved outside conditional for consistency)
      await setDoc(doc(db, 'users', userUid), {
        email: request.email,
        displayName: request.fullName,
        username: request.email.split('@')[0],
        role: 'user',
        status: 'active',
        plan: 'free',
        credits: 0,
        predictionCounts: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
      }, { merge: true }); // Use merge to update if exists
  
      await deleteDoc(doc(db, 'joinRequests', request.id));
      setJoinRequests(joinRequests.filter((req) => req.id !== request.id));
      setSelectedRequest(null);
  
      toast.success('Join request approved successfully.');
    } catch (err) {
      console.error('Error approving join request:', err);
      toast.error(err.message || 'Failed to approve join request.');
    } finally {
      setAgreeLoading(false);
    }
  };

  if (userLoading || roleLoading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-600">Loading...</div>;
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 p-6 rounded-lg shadow-md text-center">
          <p className="text-red-600 mb-4">Access denied. You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-600">Loading join requests...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 p-6 rounded-lg shadow-md text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="join-requests p-6 font-['Noto Sans'] max-w-6xl mx-auto">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <h2 className="text-3xl font-bold mb-6 text-gray-600">Join Requests</h2>
      <div className="flex justify-end mb-4">
        <button
          onClick={fetchJoinRequests}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-300"
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      {joinRequests.length === 0 ? (
        <p className="text-gray-600">No join requests found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-lg shadow-md border border-gray-300">
            <thead>
              <tr className="bg-gray-100 text-gray-600">
                <th className="p-3 text-left">Full Name</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Phone Number</th>
                <th className="p-3 text-left">Specialization</th>
                <th className="p-3 text-left">City & Country</th>
                <th className="p-3 text-left">Submitted At</th>
              </tr>
            </thead>
            <tbody>
              {joinRequests.map((request) => (
                <tr
                  key={request.id}
                  className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(request)}
                >
                  <td className="p-3">{request.fullName}</td>
                  <td className="p-3">{request.email}</td>
                  <td className="p-3">{request.phoneNumber}</td>
                  <td className="p-3">{request.specialization}</td>
                  <td className="p-3">{request.cityCountry}</td>
                  <td className="p-3">{request.createdAt.toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={handleClosePopup}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl relative max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 text-2xl font-bold"
              >
                ×
              </button>
              <h3 className="text-2xl font-bold mb-4 text-gray-600">Join Request Details</h3>
              <div className="space-y-3 text-gray-700">
                <p><strong>Full Name:</strong> {selectedRequest.fullName}</p>
                <p><strong>Clinic/Hospital:</strong> {selectedRequest.clinicHospitalName || 'N/A'}</p>
                <p><strong>Email:</strong> {selectedRequest.email}</p>
                <p><strong>Phone Number:</strong> {selectedRequest.phoneNumber}</p>
                <p><strong>Date of Birth:</strong> {selectedRequest.dateOfBirth || 'N/A'}</p>
                <p><strong>Gender:</strong> {selectedRequest.gender || 'N/A'}</p>
                <p><strong>Specialization:</strong> {selectedRequest.specialization}</p>
                <p><strong>Medical License Number:</strong> {selectedRequest.licenseNumber || 'N/A'}</p>
                <p><strong>Issuing Authority:</strong> {selectedRequest.issuingAuthority || 'N/A'}</p>
                <p><strong>Years of Experience:</strong> {selectedRequest.yearsOfExperience || 'N/A'}</p>
                <p><strong>Educational Background:</strong> {selectedRequest.education || 'N/A'}</p>
                <p><strong>Certifications:</strong> {selectedRequest.certifications || 'N/A'}</p>
                <p><strong>Address:</strong> {selectedRequest.address}</p>
                <p><strong>City & Country:</strong> {selectedRequest.cityCountry}</p>
                <p><strong>Postal Code:</strong> {selectedRequest.postalCode || 'N/A'}</p>
                <p><strong>Submitted At:</strong> {selectedRequest.createdAt.toLocaleString()}</p>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => handleDisagree(selectedRequest)}
                  className={`bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-300 ${disagreeLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={disagreeLoading || agreeLoading}
                >
                  {disagreeLoading ? 'Processing...' : 'Disagree'}
                </button>
                <button
                  onClick={() => handleAgree(selectedRequest)}
                  className={`bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-300 ${agreeLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={agreeLoading || disagreeLoading}
                >
                  {agreeLoading ? 'Processing...' : 'Agree & Create Account'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default JoinRequests;